import { ConfigProvider } from 'antd'
import CheckableTag from 'antd/es/tag/CheckableTag'
import { deriveIndicationColorState } from 'features/indicationTriage/classification/indicationClassificationColors'
import {
  createClassificationAntConfig,
  createClassificationCheckableTagStyle,
} from 'features/indicationTriage/classification/IndicationClassificationTag'
import React, { Dispatch, FC, SetStateAction, useMemo } from 'react'
import styled from 'styled-components'
import CantileverTheme from 'styles/cantileverTheme'

export interface CriticalProfileLegendProperties {
  profiles: { id: string; name: string }[]
  colors: Record<string, string>
  enabledIds?: ReadonlySet<string>
  setEnabledIds?: Dispatch<SetStateAction<ReadonlySet<string>>>
}

interface CriticalProfileLegendItemProperties {
  name: string
  color: string
  enabled: boolean
  setEnabled: (enabled: boolean) => void
}

const Container = styled.div`
  display: flex;
  flex-wrap: wrap;
`

const ItemWrapper = styled.div`
  flex: 1 1 50%; /* Each column will take up 50% of the width */
  box-sizing: border-box; /* Ensures padding and border are included in the total width and height */
  padding: 5px;
`

export const CriticalProfileLegendItem: FC<
  CriticalProfileLegendItemProperties
> = ({ name, color, enabled, setEnabled }) => {
  const colorState = useMemo(() => deriveIndicationColorState(color), [color])
  const themeConfig = useMemo(
    () => createClassificationAntConfig(colorState),
    [colorState]
  )
  const tagStyle = useMemo(
    () => createClassificationCheckableTagStyle(colorState, enabled),
    [colorState, enabled]
  )
  return (
    <ConfigProvider theme={themeConfig}>
      <CheckableTag style={tagStyle} checked={enabled} onChange={setEnabled}>
        {name}
      </CheckableTag>
    </ConfigProvider>
  )
}

export const CriticalProfileLegend: FC<CriticalProfileLegendProperties> = ({
  profiles,
  enabledIds,
  setEnabledIds,
  colors,
}) => {
  return (
    <Container>
      {profiles.map((profile) => {
        return (
          <ItemWrapper key={profile.id}>
            <CriticalProfileLegendItem
              name={profile.name}
              color={
                colors[profile.id] ?? CantileverTheme.colors.grayscale[100]
              }
              enabled={!enabledIds || enabledIds.has(profile.id)}
              setEnabled={(enabled) => {
                if (!setEnabledIds) {
                  return
                }
                if (enabled) {
                  setEnabledIds((prev) => new Set([...prev, profile.id]))
                } else {
                  setEnabledIds(
                    (prev) =>
                      new Set([...prev].filter((id) => id !== profile.id))
                  )
                }
              }}
            />
          </ItemWrapper>
        )
      })}
    </Container>
  )
}
